/* One person in Team Simplified 1a: 1 Role, 2 Accounts, 3 Exceptions.
   Everything here edits the person's per-account grants and Role links (through
   a Plan) and is a draft until Save; Copy access from and Remove live in the
   header. Picking a Role links the person to it; editing permissions by hand
   makes them Custom (CONTEXT.md: Role). */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Account, TeamRole } from '../../../api/client';
import { AccountAvatar } from '../../AccountAvatar';
import { Btn, Icon, Seg } from '../../ChatAssistant/compact/kit';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Avatar, Check, FansEditor, SearchInput } from './AccessRow';
import { NO_ACCESS, isEmpty, type Access } from './permissionModel';
import { initial } from './people';
import { PermissionEditor } from './RoleEditor';
import {
  AREAS, CUSTOM, ITEM_COUNT, NONE, SENSITIVE, accessOfRole, areaCount, itemOn, itemsOn, matchingRole,
  withAccounts, withException, withExceptionFans, withMain, withScope, withoutException,
  type Plan,
} from './roleModel';
import { Menu } from './TeamTab';

export interface DetailAccount { account_id: string; name: string }
export interface CopySource { user_id: string; name: string; sub: string }

const CHIP_LIMIT = 12;
const LIST_LIMIT = 40;

function Step({ n, title, hint, right }: { n: number; title: string; hint: string; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span className="ts-step">{n}</span>
      <span style={{ font: f(600, 13), color: C.text }}>{title}</span>
      <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>
      {right && <><div style={{ flex: 1 }} />{right}</>}
    </div>
  );
}

/** "CRM 6/8 · Chat Assistant 2/8" — what a Role switches on, in one line. */
function useIncludes() {
  const { t } = useTranslation('agency');
  return (a: Access) => {
    const parts = AREAS.map((area) => {
      const n = areaCount(a, area);
      return n ? `${t(area.labelKey)} ${n}/${area.items.length}` : null;
    }).filter(Boolean);
    const s = SENSITIVE.filter((i) => itemOn(a, i)).length;
    if (s) parts.push(t('ts.sensitiveCount', { count: s }));
    return parts.join(' · ') || t('fleet.noAccess');
  };
}

export function MemberDetail({
  name, email, plan, roles, accounts, byId, customOpen, onCustomOpen, onPlan, roleControl,
  coManagerNote, ownedNote, copySources, onCopyFrom, onRemove, onEditRole, onSaveAsRole, onBack,
}: {
  name: string;
  email: string | null;
  plan: Plan;
  roles: TeamRole[];
  /** The fleet accounts this person can be given (never ones they own). */
  accounts: DetailAccount[];
  byId: Map<string, Account>;
  customOpen: boolean;
  onCustomOpen: (open: boolean) => void;
  onPlan: (plan: Plan) => void;
  roleControl?: ReactNode;
  /** Set for an agency owner/admin: they hold every account, so no steps. */
  coManagerNote?: string | null;
  ownedNote?: string | null;
  copySources: CopySource[];
  onCopyFrom: (uid: string) => void;
  onRemove: (() => void) | null;
  onEditRole: (roleId: string) => void;
  onSaveAsRole: (access: Access) => void;
  onBack?: () => void;
}) {
  const { t } = useTranslation('agency');
  const includes = useIncludes();
  const [dd, setDd] = useState<'cp' | 'acc' | 'ex' | null>(null);
  const [q, setQ] = useState('');
  const [fansOpen, setFansOpen] = useState<string | null>(null);
  const close = () => { setDd(null); setQ(''); };
  const toggle = (k: 'cp' | 'acc' | 'ex') => { setQ(''); setDd(dd === k ? null : k); };
  const ids = accounts.map((a) => a.account_id);
  const nameOf = (id: string) => accounts.find((a) => a.account_id === id)?.name ?? id;
  const avatar = (id: string, size = 20) => {
    const meta = byId.get(id);
    return <AccountAvatar avatarUrl={meta?.avatar_url} platform={meta?.platform ?? undefined} size={size} />;
  };
  const roleById = (id: string) => roles.find((r) => r.id === id);
  const selected = customOpen ? CUSTOM : plan.ref;
  const hasRole = !isEmpty(plan.main) || customOpen;
  const match = accounts.filter((a) => !q.trim() || a.name.toLowerCase().includes(q.trim().toLowerCase()));

  const pickRole = (id: string) => {
    if (id === CUSTOM) { onCustomOpen(true); return; }
    onCustomOpen(false);
    if (id === NONE) { onPlan({ ...plan, main: NO_ACCESS, exceptions: [], ids: [] }); return; }
    const role = roleById(id);
    if (role) onPlan(withMain(plan, accessOfRole(role.grants), role.id));
  };
  // Custom grants that happen to equal a Role: offer to link them to it.
  const sameAs = selected === CUSTOM ? matchingRole(plan.main, roles) : null;
  const linkTo = (roleId: string) => { onCustomOpen(false); onPlan(withMain(plan, plan.main, roleId)); };

  const cards = [
    ...roles.map((r) => ({ id: r.id, name: r.name, desc: r.description, n: `${itemsOn(accessOfRole(r.grants))}/${ITEM_COUNT}` })),
    { id: CUSTOM, name: t('ts.custom'), desc: t('ts.customDesc'), n: selected === CUSTOM ? `${itemsOn(plan.main)}/${ITEM_COUNT}` : '' },
    { id: NONE, name: t('ts.noAccess'), desc: t('ts.noAccessDesc'), n: '' },
  ];

  // An exception on one account: any Role, No access, or the Custom grants it already has.
  const exOptions = (current: string, access: Access) => {
    const same = current === CUSTOM ? matchingRole(access, roles) : null;
    return [
      ...roles.map((r) => ({ id: r.id, label: r.name })),
      ...(current === CUSTOM ? [{ id: CUSTOM, label: same ? t('ts.customSameAs', { role: same.name }) : t('ts.custom'), disabled: true }] : []),
      { id: NONE, label: t('ts.noneShort') },
    ];
  };
  const addException = (accountId: string) => {
    const viewer = roles.find((r) => r.key === 'viewer');
    onPlan(viewer && plan.ref !== viewer.id
      ? withException(plan, accountId, accessOfRole(viewer.grants), viewer.id)
      : withException(plan, accountId, NO_ACCESS, NONE));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="ts-detail-head">
        {onBack && (
          <button type="button" className="cpt-hover-icon tm-x ts-back" onClick={onBack} title={t('ts.back')}><Icon name="arrow-left" size={15} /></button>
        )}
        <Avatar text={initial(name)} size={40} round />
        <div style={{ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span className="tm-ellipsis" style={{ font: f(600, 15), color: C.text }}>{name}</span>
            {roleControl}
          </span>
          {email && <span className="tm-ellipsis" style={{ font: f(400, 12), color: C.sub }}>{email}</span>}
        </div>
        {!coManagerNote && (
          <div style={{ position: 'relative' }}>
            <Btn kind="muted" icon="copy" onClick={() => toggle('cp')} disabled={!copySources.length}>{t('ts.copyFrom')}</Btn>
            <Menu open={dd === 'cp'} onClose={close} width={300} align="right">
              <span style={{ padding: '8px 10px 4px', font: f(400, 11), color: C.muted }}>{t('ts.copyHint')}</span>
              <div className="cpt-thin-scroll" style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflowY: 'auto' }}>
                {copySources.map((s) => (
                  <button key={s.user_id} type="button" className="cpt-hover-row tm-menu-row" onClick={() => { close(); onCopyFrom(s.user_id); }}>
                    <Avatar text={initial(s.name)} size={24} round />
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <span className="tm-ellipsis" style={{ font: f(500, 12.5), color: C.text }}>{s.name}</span>
                      <span className="tm-ellipsis" style={{ font: f(400, 11), color: C.muted }}>{s.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Menu>
          </div>
        )}
        {onRemove && <Btn kind="muted" icon="user-minus" onClick={onRemove}>{t('fleet.remove')}</Btn>}
      </div>

      {coManagerNote ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16 }}>
          <span className="ts-area-icon" style={{ background: C.tint16, color: C.purpleLt }}><Icon name="shield" size={14} /></span>
          <span style={{ font: f(500, 13), color: C.text }}>{coManagerNote}</span>
        </div>
      ) : (
        <>
          <div className="ts-step-body">
            <Step n={1} title={t('ts.step1')} hint={t('ts.step1Hint')} />
            <div className="ts-role-cards">
              {cards.map((c) => {
                const on = c.id === selected;
                return (
                  <button key={c.id} type="button" onClick={() => pickRole(c.id)} className="ts-role-card cpt-hover-border"
                    style={{ background: on ? 'color-mix(in srgb, var(--om-accent) 12%, transparent)' : C.input, borderColor: on ? C.purple : C.border }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon name={on ? 'check-circle' : 'circle'} size={14} color={on ? C.purpleLt : C.faint} />
                      <span className="tm-ellipsis" style={{ flex: 1, font: f(600, 13), color: C.text }}>{c.name}</span>
                      <span style={{ font: f(500, 11), color: C.muted }}>{c.n}</span>
                    </span>
                    <span className="ts-role-desc">{c.desc}</span>
                  </button>
                );
              })}
            </div>
            {selected !== CUSTOM && selected !== NONE && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: f(400, 11.5), color: C.muted }}>
                <span className="tm-ellipsis" style={{ flex: 1, minWidth: 0 }}>{t('ts.includes', { role: roleById(selected)?.name ?? '', list: includes(plan.main) })}</span>
                <Btn kind="text" height={24} onClick={() => onEditRole(selected)}>{t('ts.editRole')}<Icon name="chevron-right" size={12} /></Btn>
              </div>
            )}
            {sameAs && (
              <div className="ts-same-as">
                <Icon name="link" size={13} color={C.purpleLt} />
                <span style={{ flex: '1 1 220px', minWidth: 0 }}>{t('ts.sameAsHint', { role: sameAs.name })}</span>
                <Btn kind="tint" height={26} icon="link" onClick={() => linkTo(sameAs.id)}>{t('ts.linkTo', { role: sameAs.name })}</Btn>
              </div>
            )}
            {selected === CUSTOM && (
              <div className="ts-custom">
                <div className="ts-custom-head">
                  <Icon name="sliders-horizontal" size={13} color={C.purpleLt} />
                  <span style={{ font: f(600, 12.5), color: C.text }}>{t('ts.customTitle')}</span>
                  <span style={{ font: f(500, 11.5), color: C.purplePale, marginRight: 6 }}>{t('ts.permCount', { k: itemsOn(plan.main), n: ITEM_COUNT })}</span>
                  <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ts.startFrom')}</span>
                  {roles.map((r) => (
                    <button key={r.id} type="button" className="ts-pill cpt-hover-border" onClick={() => onPlan(withMain(plan, accessOfRole(r.grants), CUSTOM))}>{r.name}</button>
                  ))}
                  <button type="button" className="ts-link" onClick={() => onPlan(withMain(plan, NO_ACCESS, CUSTOM))}>{t('ts.clear')}</button>
                  <div style={{ flex: 1 }} />
                  <Btn kind="tint" icon="bookmark" height={26} disabled={isEmpty(plan.main)} title={t('ts.saveAsRoleTip')} onClick={() => onSaveAsRole(plan.main)}>{t('ts.saveAsRole')}</Btn>
                </div>
                <div style={{ padding: 14 }}>
                  <PermissionEditor access={plan.main} onChange={(next) => onPlan(withMain(plan, next, CUSTOM))} inset />
                </div>
              </div>
            )}
          </div>

          {!hasRole && (
            <div className="ts-off-note"><Icon name="alert-circle" size={14} />{t('ts.offNote', { name })}</div>
          )}

          {hasRole && (
            <div className="ts-step-body ts-step-sep">
              <Step n={2} title={t('ts.step2')} hint={t('ts.step2Hint')} right={
                <Seg<'all' | 'some'> value={plan.scope} onChange={(s) => onPlan(withScope(plan, s, ids))}
                  options={[{ id: 'all', label: t('ts.scopeAll') }, { id: 'some', label: t('ts.scopeSome') }]} />
              } />
              {ownedNote && <span style={{ font: f(400, 11.5), color: C.muted }}>{ownedNote}</span>}
              {plan.scope === 'all' ? (
                <div className="ts-box" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
                  <Icon name="layers" size={15} color={C.purpleLt} />
                  <span style={{ font: f(500, 12.5), color: C.text }}>{t('ts.allNote', { count: accounts.length })}</span>
                </div>
              ) : (
                <div className="ts-box" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: 8 }}>
                  {plan.ids.slice(0, CHIP_LIMIT).map((id) => (
                    <span key={id} className="ts-chip">
                      {avatar(id)}
                      <span className="tm-ellipsis" style={{ maxWidth: 180 }}>{nameOf(id)}</span>
                      <button type="button" className="cpt-hover-icon tm-x" style={{ width: 20, height: 20, borderRadius: 99 }} title={t('ts.removeAccount')}
                        onClick={() => onPlan(withAccounts(plan, plan.ids.filter((x) => x !== id)))}><Icon name="x" size={11} /></button>
                    </span>
                  ))}
                  {plan.ids.length > CHIP_LIMIT && <span style={{ font: f(500, 12), color: C.sub, padding: '0 4px' }}>{t('ts.moreN', { count: plan.ids.length - CHIP_LIMIT })}</span>}
                  {!plan.ids.length && !plan.exceptions.length && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 4px', font: f(400, 12), color: C.purplePale }}>
                      <Icon name="alert-circle" size={13} />{t('ts.noneYet')}
                    </span>
                  )}
                  <div style={{ position: 'relative' }}>
                    <button type="button" className="ts-add-chip cpt-hover-border" onClick={() => toggle('acc')}><Icon name="plus" size={12} />{t('ts.addAccounts')}</button>
                    <Menu open={dd === 'acc'} onClose={close} width={320}>
                      <div style={{ padding: 8, display: 'flex', gap: 6, borderBottom: `1px solid ${C.divider}` }}>
                        <SearchInput value={q} onChange={setQ} placeholder={t('ts.searchN', { count: accounts.length })} style={{ flex: 1 }} />
                        <Btn kind="tint" height={30} onClick={() => onPlan(withAccounts(plan, [...plan.ids, ...match.map((a) => a.account_id)]))}>
                          {q.trim() ? t('ts.addAllN', { count: match.length }) : t('ts.addAll')}
                        </Btn>
                      </div>
                      <div className="cpt-thin-scroll" style={{ maxHeight: 280, overflowY: 'auto', padding: 4, display: 'flex', flexDirection: 'column' }}>
                        {match.slice(0, LIST_LIMIT).map((a) => {
                          const isEx = plan.exceptions.some((e) => e.accountId === a.account_id);
                          const on = isEx || plan.ids.includes(a.account_id);
                          return (
                            <button key={a.account_id} type="button" disabled={isEx} className="cpt-hover-row tm-menu-row" style={{ height: 30, flex: 'none' }}
                              title={isEx ? t('ts.isException') : undefined}
                              onClick={() => onPlan(withAccounts(plan, on ? plan.ids.filter((x) => x !== a.account_id) : [...plan.ids, a.account_id]))}>
                              <Check state={on ? 'all' : 'none'} disabled={isEx} />
                              {avatar(a.account_id)}
                              <span className="tm-ellipsis" style={{ font: f(500, 12.5), color: C.text }}>{a.name}</span>
                            </button>
                          );
                        })}
                        {!match.length && <span style={{ padding: 8, font: f(400, 12), color: C.muted }}>{t('fleet.noMatches', { q: q.trim() })}</span>}
                      </div>
                      <div style={{ padding: '8px 10px', borderTop: `1px solid ${C.divider}`, font: f(400, 11), color: C.muted }}>
                        {match.length > LIST_LIMIT ? t('ts.showing', { k: LIST_LIMIT, n: match.length }) : t('fleet.accountCount', { count: match.length })}
                      </div>
                    </Menu>
                  </div>
                </div>
              )}
            </div>
          )}

          {hasRole && (
            <div className="ts-step-body ts-step-sep">
              <Step n={3} title={t('ts.step3')} hint={t('ts.step3Hint')} right={
                <div style={{ position: 'relative' }}>
                  <Btn kind="text" icon="plus" onClick={() => toggle('ex')}>{t('ts.addException')}</Btn>
                  <Menu open={dd === 'ex'} onClose={close} width={300} align="right">
                    <div style={{ padding: 8, borderBottom: `1px solid ${C.divider}` }}>
                      <SearchInput value={q} onChange={setQ} placeholder={t('ts.findAccount')} />
                    </div>
                    <div className="cpt-thin-scroll" style={{ maxHeight: 260, overflowY: 'auto', padding: 4, display: 'flex', flexDirection: 'column' }}>
                      {match.filter((a) => !plan.exceptions.some((e) => e.accountId === a.account_id)).slice(0, LIST_LIMIT).map((a) => (
                        <button key={a.account_id} type="button" className="cpt-hover-row tm-menu-row" style={{ height: 30, flex: 'none' }}
                          onClick={() => { close(); addException(a.account_id); }}>
                          {avatar(a.account_id)}
                          <span className="tm-ellipsis" style={{ font: f(500, 12.5), color: C.text }}>{a.name}</span>
                        </button>
                      ))}
                    </div>
                  </Menu>
                </div>
              } />
              {plan.exceptions.length > 0 ? (
                <div className="ts-box">
                  {plan.exceptions.map((e) => {
                    const ref = e.follows ? plan.ref : e.ref;
                    const fansOn = fansOpen === e.accountId;
                    return (
                      <div key={e.accountId} style={{ borderBottom: `1px solid ${C.divider}` }}>
                        <div className="ts-ex-row">
                          {avatar(e.accountId, 24)}
                          <span className="tm-ellipsis" style={{ flex: 1, minWidth: 0, font: f(500, 12.5), color: C.text }}>{nameOf(e.accountId)}</span>
                          <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ts.gets')}</span>
                          <ExceptionRole value={ref} options={exOptions(ref, e.access)}
                            onChange={(id) => {
                              const r = roleById(id);
                              onPlan(withException(plan, e.accountId, id === NONE ? NO_ACCESS : r ? accessOfRole(r.grants) : e.access, id));
                            }} />
                          <button type="button" className="ts-link" disabled={isEmpty(e.access)} title={t('fleet.fansHint')}
                            onClick={() => setFansOpen(fansOn ? null : e.accountId)}>
                            <Icon name="user" size={12} />{e.access.fans.length ? t('ts.fansN', { count: e.access.fans.length }) : t('ts.fans')}
                          </button>
                          <button type="button" className="cpt-hover-icon tm-x" title={t('ts.removeException')}
                            onClick={() => { if (!e.access.fans.length || confirm(t('fleet.confirmDropFans', { count: e.access.fans.length }))) onPlan(withoutException(plan, e.accountId)); }}>
                            <Icon name="x" size={13} />
                          </button>
                        </div>
                        {fansOn && !isEmpty(e.access) && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 12px 12px 44px' }}>
                            <span style={{ font: f(400, 11.5), color: C.sub }}>{t('fleet.fansHint')}</span>
                            <FansEditor access={e.access} onChange={(next) => onPlan(withExceptionFans(plan, e.accountId, next.fans))} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span style={{ font: f(400, 12), color: C.muted }}>{t('ts.noEx')}</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** An exception's Role: a small dropdown, so any number of Roles fits the row. */
function ExceptionRole({ value, options, onChange }: {
  value: string; options: { id: string; label: string; disabled?: boolean }[]; onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button type="button" className="cpt-hover-border ts-mini-select" style={{ height: 26 }} onClick={() => setOpen(!open)}>
        <span className="tm-ellipsis" style={{ maxWidth: 140, color: C.text }}>{current?.label ?? value}</span>
        <Icon name="chevron-down" size={12} color={C.muted} />
      </button>
      <Menu open={open} onClose={() => setOpen(false)} width={200} align="right">
        <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {options.filter((o) => !o.disabled).map((o) => (
            <button key={o.id} type="button" className="cpt-hover-row tm-menu-row" onClick={() => { setOpen(false); onChange(o.id); }}>
              <span className="tm-ellipsis" style={{ flex: 1, font: f(500, 12.5), color: C.text }}>{o.label}</span>
              <span style={{ width: 13, color: C.purpleLt }}>{o.id === value && <Icon name="check" size={13} />}</span>
            </button>
          ))}
        </div>
      </Menu>
    </div>
  );
}
